'use client'
import { cn } from '@admin/lib/utils'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid'
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react'
import * as React from 'react'
import { useCallback } from 'react'
import { DotButton, useDotButton } from './carousel-dots'

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0] & { loop?: boolean }
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  autoplayInterval?: number
  orientation?: 'horizontal' | 'vertical'
  setApi?: (api: CarouselApi) => void
  showDots?: boolean
  homePageVariant?: boolean
  largeSideArrows?: boolean
  alignStart?: boolean
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: any
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />')
  }

  return context
}

const Carousel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & CarouselProps>(
  (
    {
      orientation = 'horizontal',
      opts = { loop: true },
      setApi,
      plugins,
      className,
      children,
      autoplayInterval,
      showDots,
      homePageVariant,
      alignStart,
      ...props
    },
    ref,
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      alignStart
        ? {
            ...opts,
            axis: orientation === 'horizontal' ? 'x' : 'y',
            align: 'start',
            containScroll: 'trimSnaps',
          }
        : {
            ...opts,
            axis: orientation === 'horizontal' ? 'x' : 'y',
          },
      plugins,
    )
    const [canScrollPrev, setCanScrollPrev] = React.useState(false)
    const [canScrollNext, setCanScrollNext] = React.useState(false)

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) {
        return
      }

      setCanScrollPrev(api.canScrollPrev())
      setCanScrollNext(api.canScrollNext())
    }, [])

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev()
    }, [api])
    let timeout: any = null

    const scrollNext = React.useCallback(() => {
      api?.scrollNext()
    }, [api])

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          scrollPrev()
        } else if (event.key === 'ArrowRight') {
          event.preventDefault()
          scrollNext()
        }
      },
      [scrollPrev, scrollNext],
    )
    const handleScroll = useCallback(() => {
      scrollNext()
      if (autoplayInterval) {
        setTimeout(handleScroll, autoplayInterval)
      }
    }, [scrollNext, autoplayInterval]) // Include 'autoplayInterval' in the dependency array
    React.useEffect(() => {
      if (autoplayInterval) {
        setTimeout(handleScroll, autoplayInterval)
      }
    }, [handleScroll, autoplayInterval])
    React.useEffect(() => {
      if (!api || !setApi) {
        return
      }

      setApi(api)
    }, [api, setApi])

    React.useEffect(() => {
      if (!api) {
        return () => {}
      }

      onSelect(api)
      api.on('reInit', onSelect)
      api.on('select', onSelect)

      return () => {
        api?.off('select', onSelect)
      }
    }, [api, onSelect])

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
          showDots,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative group', className)}
          role='region'
          aria-roledescription='carousel'
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    )
  },
)
Carousel.displayName = 'Carousel'

type CarouselContentProps = {
  defaultDotsBg?: string
  activeDotsBg?: string
  dotsNoBorder?: boolean
  smallDots?: boolean
  isHover?: boolean
  isStretched?: boolean
  homePageVariant?: boolean
}

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselContentProps
>(({ className, defaultDotsBg, activeDotsBg, dotsNoBorder, smallDots, isHover, isStretched, homePageVariant, ...props }, ref) => {
  const { carouselRef, orientation, showDots, api, ...p } = useCarousel()
  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(api)
  const baseClass = cn({
    'overflow-hidden': !isStretched,
    '': !homePageVariant,
  })

  return (
    <div ref={carouselRef} className={baseClass}>
      <div
        ref={ref}
        className={cn(
          'flex',
          orientation === 'horizontal' ? (homePageVariant ? '' : '-ml-4') : '-mt-4 flex-col',
          className,
        )}
        {...props}
      />
      {scrollSnaps?.length > 0 && showDots && (
        <div
          className={cn(
            'absolute -bottom-4 md:-bottom-5 w-full flex items-center justify-center z-10 pointer-events-none',
          )}
        >
          <div className={'flex gap-1 pointer-events-auto'}>
            {scrollSnaps.map((_, index) => (
              <DotButton
                key={index}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDotButtonClick(index);
                }}
                aria-label={`Go to slide ${index + 1} of ${scrollSnaps.length}`}
                title={`Go to slide ${index + 1}`}
                style={{
                  backgroundColor: selectedIndex === index ? activeDotsBg : defaultDotsBg,
                }}
                className={cn(
                  'cursor-pointer w-2 h-2 md:w-2 md:h-2 border rounded-full pointer-events-auto',
                  {
                    'border-0': dotsNoBorder,
                  },
                  {
                    'hover:scale-125 transition-transform duration-200': isHover,
                  },
                  {
                    'w-1.5 h-1.5 md:w-2 md:h-2': smallDots,
                  },
                )}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
})
CarouselContent.displayName = 'CarouselContent'

const CarouselItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { homePageVariant?: boolean }
>(({ className, homePageVariant, ...props }, ref) => {
  const { orientation } = useCarousel()

  return (
    <div
      ref={ref}
      role='group'
      aria-roledescription='slide'
      className={cn(
        'min-w-0 shrink-0 grow-0 basis-full',
        orientation === 'horizontal' ? (homePageVariant ? '' : 'md:pl-4') : 'pt-4',
        className,
      )}
      {...props}
    />
  )
})
CarouselItem.displayName = 'CarouselItem'

// Define an interface for CarouselPrevious and CarouselNext props
interface CarouselButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  isStretched?: boolean // Define isStretched as an optional boolean
  stretchedClass?: string
  isMwebArrow?: boolean
  isFaded?: boolean
  homePageVariant?: boolean
  largeSideArrows?: boolean
}

const CarouselPrevious = React.forwardRef<HTMLButtonElement, CarouselButtonProps>(
  (
    {
      className,
      isStretched,
      stretchedClass,
      isMwebArrow = false,
      isFaded = false,
      homePageVariant,
      largeSideArrows,
      ...props
    },
    ref,
  ) => {
    const { orientation, scrollPrev, canScrollPrev } = useCarousel()

    if (!canScrollPrev) {
      return <></>
    }

    const buttonStyles = cn(`border-none shadow h-8 w-8 rounded-full flex items-center justify-center `, {
      'md:h-12 md:w-12 -mr-14': largeSideArrows,
      'md:h-10 md:w-10': !largeSideArrows,
    })

    const stretchedStyles = isStretched ? 'bg-white' : ''
    const stretchedClassName = cn(
      'absolute top-0 bottom-0 flex flex-col items-center justify-center',
      {
        'opacity-5 duration-200 transition-opacity group-hover:opacity-100 bg-gradient-to-r from-black/60 p-4 left-0':
          !isStretched && !isMwebArrow,
        'opacity-100 p-0 -left-2 hidden sm:flex': isStretched && !isMwebArrow,
        'opacity-100 p-0 sm:-left-6 left-[54px] flex': isMwebArrow && isStretched,
      },
      stretchedClass,
    )

    return (
      <div
        onClick={event => {
          event.stopPropagation()
          scrollPrev()
        }}
        className={stretchedClassName}
      >
        <button
          ref={ref}
          className={cn(buttonStyles, stretchedStyles, className)}
          disabled={!canScrollPrev}
          {...props}
        >
          <ChevronLeftIcon
            className={cn({
              'fill-black': isStretched,
              'fill-white': !isStretched,
              'h-14 w-14': largeSideArrows,
              'h-6 w-6': !largeSideArrows,
            })}
          />
          <span className='sr-only'>Previous slide</span>
        </button>
        {isFaded && <div className='absolute top-0 bottom-0 -left-6 w-[72px] bg-custom-l-gradient'></div>}
      </div>
    )
  },
)
CarouselPrevious.displayName = 'CarouselPrevious'

const CarouselNext = React.forwardRef<HTMLButtonElement, CarouselButtonProps>(
  (
    {
      className,
      isStretched,
      stretchedClass,
      isMwebArrow = false,
      isFaded = false,
      homePageVariant,
      largeSideArrows,
      ...props
    },
    ref,
  ) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel()

    if (!canScrollNext) {
      return <></>
    }

    const stretchedStyles = isStretched ? 'bg-white' : ''
    const stretchedClassName = cn(
      'absolute top-0 bottom-0 flex flex-col items-center justify-center',
      {
        'opacity-5 duration-200 transition-opacity group-hover:opacity-100 bg-gradient-to-l from-black/60 p-4 right-0':
          !isStretched && !isMwebArrow,
        'opacity-100 p-0 -right-2 hidden sm:flex': isStretched && !isMwebArrow,
        'opacity-100 p-0 sm:-right-6 right-5 flex': isMwebArrow && isStretched,
      },
      stretchedClass,
    )

    return (
      <div
        onClick={event => {
          event.stopPropagation()
          scrollNext()
        }}
        className={stretchedClassName}
      >
        <button
          ref={ref}
          className={cn(
            `border-none shadow h-8 w-8 rounded-full flex items-center justify-center`,
            className,
            {
              'md:h-12 md:w-12 -ml-14': largeSideArrows,
              'md:h-10 md:w-10': !largeSideArrows,
              'bg-white': isStretched,
            },
          )}
          disabled={!canScrollNext}
          {...props}
        >
          <ChevronRightIcon
            className={cn({
              'h-6 w-6': !largeSideArrows,
              'h-14 w-14': largeSideArrows,
              'fill-black': isStretched,
              'fill-white': !isStretched,
            })}
          />
          <span className='sr-only'>Next slide</span>
        </button>
        {isFaded && <div className='absolute top-0 bottom-0 w-[72px] bg-custom-r-gradient'></div>}
      </div>
    )
  },
)
CarouselNext.displayName = 'CarouselNext'

export { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi }

